import { useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import Seo from "@/components/feature/Seo";

type FormResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahbm0rp14a1h3mr6hsg";

const CONTACT_CARDS = [
  {
    icon: "ri-megaphone-line",
    title: "线索投稿",
    desc: "写明事件大致时间、信息来源与可核实的公开链接，我们会先做基础核实。",
  },
  {
    icon: "ri-edit-2-line",
    title: "内容更正",
    desc: "如发现内容存在偏差，请指出具体段落并提供依据，核实后我们会尽快修订并留痕。",
  },
  {
    icon: "ri-group-line",
    title: "内容合作",
    desc: "选题共建、内容授权与转载事宜，欢迎说明合作方向与预期形式。",
  },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "联系我们 - 看料吃瓜社",
    url: "https://kanliaochigua.example/contact",
    inLanguage: "zh-CN",
    description: "看料吃瓜社联系页面，提供线索投稿、内容更正与合作咨询的表单与邮箱。",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://kanliaochigua.example/" },
      { "@type": "ListItem", position: 2, name: "联系我们", item: "https://kanliaochigua.example/contact" },
    ],
  },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("已收到你的信息，我们会在两个工作日内回复。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") {
        return;
      }
      const text = String(value);
      if (text.trim() === "") {
        return;
      }
      payload.append(key, text);
    });

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(String(serverMsg));

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("已收到你的信息，我们会在两个工作日内回复。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后再试，或直接发送邮件联系我们。");
      }
    } catch (error) {
      setStatus("error");
      setMessage("网络异常，请稍后再试，或直接发送邮件联系我们。");
    }
  };

  return (
    <>
      <Seo
        title="联系我们 | 线索投稿与内容合作 - 看料吃瓜社"
        description="通过联系表单向看料吃瓜社提交线索、反馈内容更正或洽谈内容合作。请写明事件时间、信息来源与可核实的公开链接，我们通常会在两个工作日内回复。"
        keywords="看料吃瓜联系,线索投稿,内容更正,内容合作,联系表单"
        path="/contact"
        jsonLd={jsonLd}
      />

      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            联系我们
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            无论是手上有一条值得整理的线索，还是发现内容需要更正，都可以直接通过下面的表单告诉我们。信息越具体，核实越快。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-5">你可以这样找我们</h2>
            <div className="space-y-4">
              {CONTACT_CARDS.map((item) => (
                <div key={item.title} className="flex gap-4 p-5 bg-background-50 border border-background-200 rounded-lg">
                  <span className="w-11 h-11 shrink-0 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center text-xl">
                    <i className={item.icon}></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 p-5 bg-secondary-950 rounded-lg">
              <h3 className="font-heading text-base font-semibold text-background-50 mb-2 inline-flex items-center gap-2">
                <i className="ri-mail-line text-accent-400"></i>
                邮箱直连
              </h3>
              <p className="text-sm text-background-300/90 leading-relaxed mb-3">
                不方便填表？直接把线索发到邮箱也可以，标题注明「线索投稿」即可。
              </p>
              <a
                href="mailto:contact@kanliaochigua.example"
                rel="nofollow"
                className="text-sm font-label text-accent-300 hover:text-accent-200 transition-colors break-all"
              >
                contact@kanliaochigua.example
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              id="contact-inquiry-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="p-6 md:p-8 bg-background-50 border border-background-200 rounded-lg"
              noValidate
            >
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-1">提交线索或咨询</h2>
              <p className="text-xs text-foreground-500 mb-6">带 * 的字段为必填项，我们会在两个工作日内回复。</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-label font-medium text-foreground-800 mb-2">
                    称呼 *
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    autoComplete="name"
                    placeholder="怎么称呼你"
                    className="w-full px-3.5 py-2.5 text-sm font-label rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-label font-medium text-foreground-800 mb-2">
                    邮箱 *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    placeholder="用于接收回复"
                    className="w-full px-3.5 py-2.5 text-sm font-label rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-type" className="block text-sm font-label font-medium text-foreground-800 mb-2">
                  咨询类型 *
                </label>
                <select
                  id="contact-type"
                  name="type"
                  required
                  defaultValue="线索投稿"
                  className="w-full px-3.5 py-2.5 text-sm font-label rounded-md bg-background-100 border border-background-200 text-foreground-900 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors cursor-pointer"
                >
                  <option value="线索投稿">线索投稿</option>
                  <option value="内容更正">内容更正</option>
                  <option value="内容合作">内容合作</option>
                  <option value="其他咨询">其他咨询</option>
                </select>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-message" className="block text-sm font-label font-medium text-foreground-800 mb-2">
                  具体内容 *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  placeholder="请写明事件大致时间、信息来源与可核实的公开链接（最多 500 字）"
                  className="w-full px-3.5 py-2.5 text-sm font-label rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors resize-none"
                ></textarea>
                <p className="mt-1.5 text-[11px] font-label text-foreground-400">最多 500 字。</p>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="field-honeypot"
              />

              <button
                type="submit"
                disabled={status === "loading"}
                className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-label font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer"
              >
                <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                {status === "loading" ? "提交中…" : "提交信息"}
              </button>

              {status === "success" && (
                <p className="mt-4 p-3.5 rounded-md bg-secondary-100 text-secondary-900 text-sm font-label flex items-start gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5"></i>
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="mt-4 p-3.5 rounded-md bg-primary-100 text-primary-800 text-sm font-label flex items-start gap-2">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  {message}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
}